<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'WorkSummaryViewFilters',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'submit']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref({
    date_from: null,
    date_to: null,
  });
  const ux = ref([]);
  const b_8564662037462133_style = computed(() => ({
    width: '500px',
  }));

  const $data = computed(() => ({
    item: item.value,
    ux: ux.value,
    b_8564662037462133_style: b_8564662037462133_style.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item', cache: { type: 'local', key: 'WorkSummaryViewFilters' } },
      { name: 'ux' },
      { name: 'b_8564662037462133_style', nodeId: '8564662037462133' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { item, ux, b_8564662037462133_style },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const act_8564662037462133_setData_liuyn2atn_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.date_from',
      value: $event.data[0],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_8564662037462133_setData_m23l2kadm_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.date_to',
      value: $event.data[1],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3943151158818434_emit_w19pv1t86_getActionArgs = async ($event) => {
    return {
      event: 'submit',
      value: item.value,
    };
  };

  const act_7315092382398562_reloadComponentData_9h1q6givn =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_c0ambllia =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_9h1q6givn,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_c0ambllia],
    event: 'reload',
  });
  const act_8564662037462133_setData_liuyn2atn = new actionTypes.SetDataAction({
    actionArgs: act_8564662037462133_setData_liuyn2atn_getActionArgs,
  });
  const inst_evh_8564662037462133_changed_v3fyhb0rv =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_setData_liuyn2atn,
      event: 'changed',
      displayName: 'setData',
    });
  const act_8564662037462133_setData_m23l2kadm = new actionTypes.SetDataAction({
    actionArgs: act_8564662037462133_setData_m23l2kadm_getActionArgs,
  });
  const inst_evh_8564662037462133_changed_kxhxdeqq8 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_setData_m23l2kadm,
      event: 'changed',
      displayName: 'setData',
    });
  const inst_evh_8564662037462133_changed = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_8564662037462133_changed_v3fyhb0rv,
      inst_evh_8564662037462133_changed_kxhxdeqq8,
    ],
    event: 'changed',
  });
  const act_3943151158818434_emit_w19pv1t86 = new actionTypes.EmitAction({
    actionArgs: act_3943151158818434_emit_w19pv1t86_getActionArgs,
    displayName: 'load',
  });
  const inst_evh_3943151158818434_clickPreventStop_0y1zshfsg =
    new actionTypes.EventHandlerImpl({
      action: act_3943151158818434_emit_w19pv1t86,
      event: 'click.prevent.stop',
      displayName: 'load',
    });
  const inst_evh_3943151158818434_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3943151158818434_clickPreventStop_0y1zshfsg],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_c0ambllia,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_changed_v3fyhb0rv,
    inst_evh_8564662037462133_changed_kxhxdeqq8,
    inst_evh_8564662037462133_changed,
    inst_evh_3943151158818434_clickPreventStop_0y1zshfsg,
    load: inst_evh_3943151158818434_clickPreventStop_0y1zshfsg,
    inst_evh_3943151158818434_clickPreventStop,
  };

  const evh_7315092382398562_reload_c0ambllia = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_c0ambllia.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_changed_v3fyhb0rv = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_changed_v3fyhb0rv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_changed_kxhxdeqq8 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_changed_kxhxdeqq8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_changed = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_clickPreventStop_0y1zshfsg = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3943151158818434_clickPreventStop_0y1zshfsg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3943151158818434_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox v-if="loaded">
      <DateRangeSelector
        name="paymentFilters"
        :from="item.date_from"
        :to="item.date_to"
        class="me-2"
        :style="b_8564662037462133_style"
        @changed="evh_8564662037462133_changed($event, {})"
      >
      </DateRangeSelector>
      <div>
        <ItpButton
          variant="light"
          :icon="$config.reloadIcon"
          :title="pgettext('WorkSummaryViewFilters', 'Load data')"
          :spinning="actions && actions.load && actions.load.isRunning"
          @click.prevent.stop="
            evh_3943151158818434_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </div>
    </ItpBox>
  </ItpUsercontrol>
</template>
